/**
 * @description : Handle all tracking requests.
 *
 * @requires  {Object} trackingService
 * @requires  {Object} numberService
 * @requires  {Object} applicationModel
 * @requires  {Object} trackingCodeService
 * @requires  {Object} parcelsService
 * @requires  {Object} notificationService
 * @requires  {Function} $location
 * @requires  {Object} $rootScope
 * @requires  {Object} $cookies
 * @requires  {Object} $q
 * @returns   {Object} routeObject - object containing functions to resolve routes.
 */
function routeService(
  trackingService,
  numberService,
  applicationModel,
  trackingCodeService,
  parcelsService,
  notificationService,
  $location,
  $rootScope,
  $cookies,
  $q) {

  /**
   * Handle the possible errors in a valid response or rejection.
   *
   * @param {Object} response
   * @param {String} trackingNumber
   */
  function handleErrors(response, trackingNumber) {

    if (response[0]) {

      applicationModel.urlRequest.error = response[0];
      applicationModel.urlRequest.number = trackingNumber;
      $location.path('/');

    }

    if (response.data && response.data.errors) {

      for (let i = 0; response.data.errors.length; i++) {

        if (response.data.errors[i].errorDescription !== '') {

          applicationModel.urlRequest.error = response.data.errors[i].errorDescription;
          break;

        }

      }

      applicationModel.urlRequest.number = trackingNumber;
      $location.path('/');

    }

    if (response.error) {

      applicationModel.urlRequest.error = response.error;
      applicationModel.urlRequest.number = trackingNumber;
      $location.path('/');

    }

  }

  const routeObject = {

    /**
     * Check the model isn't empty if it is redirect to home page.
     */
    checkModel: () => {

      if (Object.keys(applicationModel.trackingDetails).length === 0) {

        $location.path('/');

      }

    },

    /**
     * @description : Call tracking service with tracking number from URL.
     * @param {String} trackingNumber
     * @return {Promise}
     */
    resolveURL: (trackingNumber) => {

      const deferred = $q.defer();
      const sanitizedTrackingNumber = numberService.sanitize(trackingNumber);
      const isValidNumber = numberService.validate(sanitizedTrackingNumber);
      const googleRecaptchaCheckRequired = trackingCodeService.triggerGoogleRecaptchaCheck();
      let isMissMatch = true;
      applicationModel.urlSearch = true;

      if (angular.isDefined(applicationModel.trackingDetails.mailPieces) &&
        (applicationModel.trackingDetails.mailPieces.mailPieceId === trackingNumber)) {

        isMissMatch = false;

      }

      if (isValidNumber === true) {

        if (googleRecaptchaCheckRequired && !applicationModel.macSessionHeader) {

          // Redirect to the landing page, which will trigger captcha to init.
          $location.path('/');
          applicationModel.stopUrlRequest = false;

          // Emit when the google recaptcha widget is available.
          $rootScope.$on('googleRecaptchaWidgetAvailable', () => {

            $rootScope.$emit('externalReSubmit', sanitizedTrackingNumber);

          });

        } else if (isMissMatch || applicationModel.macSessionHeader) {

          deferred.resolve(routeObject.sendRequest(sanitizedTrackingNumber));

        // This is to ensure we don't send a request if we already have the tracking information.

        } else if (angular.isDefined(applicationModel.trackingDetails.mailPieces)) {

          deferred.resolve();

        }

      // Send to Landing page.

      } else if (isValidNumber === 'checkPFW') {

        applicationModel.urlRequest.number = sanitizedTrackingNumber;
        $location.path('/');
        $rootScope.$emit('externalReSubmit', sanitizedTrackingNumber);

      } else {

        applicationModel.urlRequest.number = sanitizedTrackingNumber;
        applicationModel.urlRequest.error = numberService.errorMessageMap(isValidNumber);

        // Redirect to the landing page to handle the errors.
        $location.path('/');

      }

      return deferred.promise;

    },

    sendRequest: trackingNumber => trackingService.getEvents(trackingNumber).then((response) => {

      handleErrors(response, trackingNumber);

      if (response.data) {

        applicationModel.trackingDetails = response.data;
        applicationModel.estimatedDelivery = response.data.mailPieces.estimatedDelivery;

      }

    }).catch((rejection) => {

      handleErrors(rejection, trackingNumber);

    }),

    /**
     * @description : Check if the current request is a MAC URL request and redirect to normal request.
     * @param  {Object} $route
     * @returns {Function} handleRequest after setting MAC header if needed.
     */
    handleNotification: ($route) => {

      const urlMac = angular.copy($route.current.params.mac);
      let macHeader = '';

      applicationModel.sanitizedNumber = angular.copy($route.current.params.number);

      // Check if there is an existing valid session.
      if (applicationModel.googleRecaptcha.sessionToken) {

        const sessionExp = parcelsService.decodeJWT(applicationModel.googleRecaptcha.sessionToken).exp;

        if (notificationService.isNewSessionNeeded(sessionExp)) {

          // Set the MAC as header for an invalid session.
          macHeader = notificationService.decodeMAC(urlMac);

        }

      } else {

        // Set the MAC as header for an invalid session.
        macHeader = notificationService.decodeMAC(urlMac);

      }

      // Reset macSessionHeader.
      delete applicationModel.macSessionHeader;

      if (macHeader !== '') {

        // Add the session token to the ApplicationModel for future use.
        applicationModel.macSessionHeader = macHeader;

      }

      return routeObject.handleRequest($route);

    },

    /**
     * @description : Check if the current request is a URL request and check model isn't empty
     * @param  {Object} $route
     * @returns {Function} Tracking request if number is set in URL.
     */
    handleRequest: ($route) => {

      routeObject.resetErrors();

      applicationModel.sanitizedNumber = angular.copy($route.current.params.number);

      // Set authToken if the parcels_jwt cookie was found and is valid.
      applicationModel.authToken = (parcelsService.isValidCookie()) ? $cookies.get('parcels_jwt') : null;

      if (applicationModel.sanitizedNumber) {

        return routeObject.resolveURL(applicationModel.sanitizedNumber);

      }

      return routeObject.checkModel();

    },

    /**
     * @description : Empties application model so that next request is clean.
     */
    resetErrors: () => {

      applicationModel.urlRequest = {};

    }

  };

  return routeObject;

}

export default routeService;



// WEBPACK FOOTER //
// ./src/services/route.service.js